/**
 * LOT-UI-03b.1 — Post-login store picker (DS Remobiles, responsive).
 */
import React, { useState } from 'react';
import { Store, Globe2, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

import { usePermissions } from '../contexts/PermissionsContext';

function MagasinPickSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="magasin-session-loading">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-24 rounded-xl animate-pulse"
          style={{ background: 'var(--rm-bg-elevated)', border: '1px solid var(--rm-border-subtle)' }}
        />
      ))}
    </div>
  );
}

export const MagasinSessionPicker = () => {
  const {
    isSuper, isAdminMag, assignedMagasins, allMagasins,
    loadingMagasins, setSessionMagasin,
  } = usePermissions();

  const [busy, setBusy] = useState(null); // code en cours de sélection

  // admin_magasin : tous les magasins actifs (admin sur assignés, vente ailleurs).
  const candidates = allMagasins;

  // ISOLATION — la sélection lie la boutique à la SESSION côté serveur (select-active
  // -> nouveau JWT signé). On attend le résultat avant de fermer et on refuse une
  // boutique hors périmètre.
  const pick = async (codeOrAll) => {
    setBusy(codeOrAll || 'ALL');
    try {
      const res = await setSessionMagasin(codeOrAll);
      if (res && res.ok === false) {
        toast.error(res.error || 'Sélection refusée');
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="magasin-session-picker"
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4 md:p-6 overflow-auto"
      style={{ background: 'rgba(6, 8, 13, 0.88)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="w-full sm:max-w-2xl lg:max-w-3xl rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 sm:p-6 md:p-8 max-h-[92vh] overflow-y-auto"
        style={{
          background: 'var(--rm-bg-panel)',
          border: '1px solid var(--rm-border-subtle)',
        }}
      >
        <header className="mb-5 sm:mb-6 text-center sm:text-left">
          <div
            className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl mb-3"
            style={{ background: 'var(--rm-red-muted)', border: '1px solid var(--rm-border-strong)' }}
          >
            <Store className="w-6 h-6 text-[var(--rm-red-light)]" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-[var(--rm-white)] mb-1">
            Choix du magasin de session
          </h1>
          <p className="text-sm text-[var(--rm-gray-400)] max-w-lg mx-auto sm:mx-0">
            {isAdminMag
              ? 'Choisissez le magasin de session. Vous pouvez encaisser sur tout magasin actif (mode vente hors magasin assigné).'
              : 'Indiquez le magasin où vous êtes. Cela détermine la caisse, les tickets et le stock affiché.'}
          </p>
        </header>

        {loadingMagasins ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--rm-gray-400)]" />
            <p className="text-sm text-[var(--rm-gray-500)]">Chargement des magasins…</p>
            <MagasinPickSkeleton />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {isSuper && (
              <button
                type="button"
                onClick={() => pick('')}
                disabled={!!busy}
                data-testid="magasin-session-pick-all"
                className="flex flex-col items-start justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 min-h-[88px] text-left transition hover:bg-white/[0.03] active:scale-[0.99]"
                style={{
                  borderColor: 'rgba(22, 163, 74, 0.45)',
                  background: 'var(--rm-success-muted)',
                  color: 'var(--rm-success-foreground)',
                }}
              >
                <Globe2 className="w-6 h-6 text-[var(--rm-success)]" />
                <span className="font-bold text-[var(--rm-white)]">Tous (vue globale)</span>
                <span className="text-xs text-[var(--rm-gray-400)]">Directeur · super-admin</span>
              </button>
            )}
            {candidates.map((m) => {
              const accent = m.color || 'var(--rm-gray-500)';
              return (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => pick(m.code)}
                  disabled={!!busy}
                  data-testid={`magasin-session-pick-${m.code}`}
                  className="relative flex flex-col items-start justify-center gap-1 rounded-xl border px-4 py-5 min-h-[88px] text-left transition hover:bg-white/[0.03] active:scale-[0.99] overflow-hidden"
                  style={{
                    borderColor: 'var(--rm-border-subtle)',
                    background: 'var(--rm-bg-elevated)',
                  }}
                >
                  <span
                    className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
                    style={{ background: accent }}
                    aria-hidden
                  />
                  {busy === m.code ? (
                    <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-[var(--rm-gray-400)]" />
                  ) : null}
                  <span className="text-2xl sm:text-3xl font-black tracking-tight text-[var(--rm-white)] pl-2">
                    {m.code}
                  </span>
                  <span className="text-sm font-semibold text-[var(--rm-gray-100)] pl-2">{m.name}</span>
                  {m.address ? (
                    <span className="text-[11px] text-[var(--rm-gray-500)] pl-2 flex items-start gap-1 line-clamp-2">
                      <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      {m.address}
                    </span>
                  ) : null}
                </button>
              );
            })}
            {candidates.length === 0 && !isSuper && (
              <div className="col-span-full text-center py-10 text-[var(--rm-gray-400)]">
                Aucun magasin assigné. Contactez l&apos;administrateur.
              </div>
            )}
          </div>
        )}

        <footer className="mt-5 sm:mt-6 pt-4 border-t text-center sm:text-left text-xs text-[var(--rm-gray-500)]" style={{ borderColor: 'var(--rm-border-subtle)' }}>
          Vous pourrez changer de magasin via le badge en haut à droite.
        </footer>
      </div>
    </div>
  );
};

export default MagasinSessionPicker;
